import { z } from "zod/v4";
import { clubRoleSchema, membershipStatusSchema, memberTypeSchema } from "./shared";

export const clubMemberSchema = z.object({
	id: z.string().uuid(),
	club_id: z.string().uuid(),
	user_id: z.string().uuid(),
	role: clubRoleSchema,
	member_type: memberTypeSchema,
	status: membershipStatusSchema,
	display_name: z.string(),
	email: z.string(),
	user_avatar_url: z.string().nullable().optional(),
	skill_level_id: z.string().uuid().nullable().optional(),
	skill_level_name: z.string().nullable().optional(),
	birthday: z.coerce.date().nullable().optional(),
	membership_type_id: z.string().uuid().nullable().optional(),
	membership_type_name: z.string().nullable().optional(),
	fee_override_cents: z.number().int().nullable().optional(),
	trial_expires_at: z.coerce.date().nullable().optional(),
	joined_at: z.coerce.date().nullable().optional(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const updateClubMemberSchema = z.object({
	role: clubRoleSchema.optional(),
	member_type: memberTypeSchema.optional(),
	status: membershipStatusSchema.optional(),
	skill_level_id: z.string().uuid().nullable().optional(),
	birthday: z.string().nullable().optional(),
	membership_type_id: z.string().optional(),
	fee_override_cents: z.number().int().nonnegative().optional(),
	clear_fee_override: z.boolean().optional(),
	trial_expires_at: z.string().optional(),
});

export const bulkUpdateMembersSchema = z
	.object({
		user_ids: z.array(z.string().uuid()).min(1),
		status: membershipStatusSchema.optional(),
		member_type: memberTypeSchema.optional(),
	})
	.refine((v) => v.status !== undefined || v.member_type !== undefined, {
		message: "at least one of status or member_type is required",
	});

export const bulkUpdateMembersResponseSchema = z.object({
	updated: z.number(),
	failed: z.number(),
	results: z.array(
		z.object({
			user_id: z.string().uuid(),
			success: z.boolean(),
			error: z.string().optional(),
		}),
	),
});

export const memberCountsSchema = z.object({
	by_role: z.object({
		admin: z.number(),
		member: z.number(),
		organizer: z.number(),
		owner: z.number(),
		trainer: z.number(),
	}),
	by_status: z.object({
		active: z.number(),
		cancelled: z.number(),
		declined: z.number(),
		invited: z.number(),
		paused: z.number(),
		pending: z.number(),
	}),
	total: z.number(),
});

export type MemberCounts = z.infer<typeof memberCountsSchema>;

export const paginatedClubMembersSchema = z.object({
	items: z.array(clubMemberSchema),
	total: z.number(),
	limit: z.number(),
	offset: z.number(),
});

export const userSearchResultSchema = z.object({
	id: z.string().uuid(),
	display_name: z.string(),
	email: z.string(),
	avatar_url: z.string().nullable().optional(),
});
